import formatDate from "date-fns/format";
import capitalizeString from "lodash/capitalize";
import React, { Children } from "react";
import { useTranslation } from "react-i18next";

import timeAgo from "./timeAgo";
import useThemeProps from "./useThemeProps.jsx";
import useTicker from "./useTicker.jsx";

function fixSwedishFormat(string) {
  return string.replace(/\.$/, "");
}

function getMachineDateString(date, precision) {
  const { [precision]: format } = {
    year: "yyyy",
    month: "yyyy-MM",
    day: "yyyy-MM-dd",
    hour: "yyyy-MM-dd HH",
    minute: "yyyy-MM-dd HH:mm",
    second: "yyyy-MM-dd HH:mm:SS",
  };
  return formatDate(date, format);
}

function getHumanDateString(date, locale, format) {
  let humanDate = date.toLocaleString(locale, format);

  if (locale.startsWith("sv")) {
    humanDate = fixSwedishFormat(humanDate);
  }

  return humanDate;
}

function Ago({ date }) {
  useTicker(30 * 1000);
  return timeAgo.format(date);
}

export default function Time(props) {
  props = useThemeProps({ props, name: "Time" });
  let {
    ago = false,
    capitalize,
    date,
    format,
    locale,
    children,
    precision,
    as: Component = "time",
    ...restProps
  } = props;
  date = new Date(date);
  if (isNaN(date)) {
    return null;
  }

  const { i18n } = useTranslation();

  if (locale == null) {
    locale = i18n.language;
  }

  let humanDate = getHumanDateString(date, locale, format);

  precision ??=
    ago || !format || format.timeStyle || format.second
      ? "second"
      : format.minute
      ? "minute"
      : format.hour
      ? "hour"
      : format.dateStyle || format.day
      ? "day"
      : format.month
      ? "month"
      : "year";

  const machineDate = getMachineDateString(date, precision);

  const ownerState = {
    ago,
    capitalize,
    date,
    format,
    locale,
    precision,
    humanDate,
    machineDate,
    getMachineDateString,
    getHumanDateString,
  };

  if (typeof children === "function") {
    children = children(ownerState);
  } else if (Children.count(children) > 0) {
    throw new Error(
      `<Time> children should be empty or a function, but got ${children}`,
    );
  } else {
    children = ago ? (
      <>
        <Ago date={date} capitalize={capitalize} />
        {format ? ` (${humanDate})` : null}
      </>
    ) : capitalize ? (
      capitalizeString(humanDate)
    ) : (
      humanDate
    );
  }

  return (
    <Component dateTime={machineDate} {...restProps}>
      {children}
    </Component>
  );
}
